import { useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';

import { setAppLanguage, type SupportedLng } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/utils';
import { DropdownMenu, MenuItem, MenuLabel } from '@/shared/ui/dropdown-menu';
import { Icon } from '@/shared/ui/icon';

import { CzFlag, GbFlag, type FlagProps } from './Flags';

interface LanguageMeta {
  code: SupportedLng;
  /** Krátký jazykově neutrální kód zobrazený v menu. */
  label: string;
  Flag: ComponentType<FlagProps>;
}

const LANGUAGES: readonly LanguageMeta[] = [
  { code: 'cs', label: 'CZE', Flag: CzFlag },
  { code: 'en', label: 'ENG', Flag: GbFlag },
];

/** Vlajka v jemném rámečku se zaoblenými rohy (sjednocený vzhled v triggeru i v menu). */
function FlagChip({ Flag, size = 18 }: { Flag: ComponentType<FlagProps>; size?: number }) {
  return (
    <span className="inline-flex overflow-hidden rounded-[2px] shadow-[0_0_0_1px_var(--phx-border-3)]">
      <Flag size={size} />
    </span>
  );
}

export interface LanguageSwitcherProps {
  className?: string;
  /** Zarovnání dropdownu vůči triggeru. Výchozí `end`. */
  align?: 'start' | 'end';
}

/**
 * Kompaktní přepínač jazyka — trigger ukazuje vlajku aktivního jazyka, menu nabízí
 * CZE / ENG. Volbu přepíná přes i18next a ukládá do `localStorage` (přežije reload).
 */
export function LanguageSwitcher({ className, align = 'end' }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation('sharedUi');
  const [open, setOpen] = useState(false);

  const activeCode = (i18n.resolvedLanguage ?? i18n.language) as string;
  const active = LANGUAGES.find((l) => l.code === activeCode) ?? LANGUAGES[0];

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      align={align}
      className="min-w-[120px]"
      trigger={
        <button
          type="button"
          title={t('language.aria')}
          aria-label={t('language.aria')}
          className={cn(
            'phx-language-switcher gap-phx-2 rounded-phx-sm px-phx-2 inline-flex h-6 cursor-pointer items-center border-0 bg-transparent',
            'text-phx-text-muted hover:text-phx-text hover:bg-phx-surface-2 transition-colors',
            className
          )}
        >
          <FlagChip Flag={active.Flag} />
          <Icon name="chevdown" size={12} className="opacity-70" />
        </button>
      }
    >
      <MenuLabel>{t('language.label')}</MenuLabel>
      {LANGUAGES.map(({ code, label, Flag }) => (
        <MenuItem
          key={code}
          active={code === active.code}
          onClick={() => {
            setAppLanguage(code);
            setOpen(false);
          }}
        >
          <span className="gap-phx-2 flex items-center">
            <FlagChip Flag={Flag} size={20} />
            {label}
          </span>
        </MenuItem>
      ))}
    </DropdownMenu>
  );
}
